/**
 * Grindstone (vanilla GrindstoneMenu): strips every non-curse enchantment and
 * refunds XP, or merges two matching damageable items (remaining durability
 * + 5% of max, keeps curses only). Pure; the screen owns slots and XP payout.
 */

import { I } from '../itemIds';
import { maxDamageOf } from '../itemdb';
import { cloneStack, isEmpty, type Stack } from '../types';
import { E, enchDef, minCost } from './registry';

/** Curse enchantments survive a grindstone. */
export const CURSE_ENCHANTS: ReadonlySet<number> = new Set<number>([E.BINDING_CURSE, E.VANISHING_CURSE]);

export interface GrindstoneResult {
  out: Stack;
  /** Upper bound of the XP refund; the payout is ceil(xp / 2) + random(ceil(xp / 2)). */
  xp: number;
}

function stripped(s: Stack): Stack {
  const o = cloneStack(s);
  const keep = (s.ench ?? []).filter((e) => CURSE_ENCHANTS.has(e.id));
  if (keep.length) o.ench = keep; else delete o.ench;
  delete o.rep;
  return o;
}

function xpOf(s: Stack): number {
  let xp = 0;
  for (const e of s.ench ?? []) {
    const d = enchDef(e.id);
    if (d && !CURSE_ENCHANTS.has(e.id)) xp += minCost(d, e.lvl);
  }
  return xp;
}

export function grindstoneCombine(a: Stack, b: Stack): GrindstoneResult | null {
  const ea = isEmpty(a), eb = isEmpty(b);
  if (ea && eb) return null;
  const xp = (ea ? 0 : xpOf(a)) + (eb ? 0 : xpOf(b));
  if (ea !== eb) {
    const s = ea ? b : a;
    if (xpOf(s) <= 0) return null;
    const out = stripped(s);
    out.count = 1;
    if (s.id === I.ENCHANTED_BOOK) out.id = I.BOOK;
    return { out, xp };
  }
  if (a.id !== b.id) return null;
  const max = maxDamageOf(a.id);
  if (max <= 0) return null;
  const out = stripped(a);
  out.count = 1;
  const remain = (max - a.dmg) + (max - b.dmg) + Math.floor(max * 5 / 100);
  out.dmg = Math.max(0, max - remain);
  const keep = new Map<number, number>();
  for (const e of [...(a.ench ?? []), ...(b.ench ?? [])]) if (CURSE_ENCHANTS.has(e.id)) keep.set(e.id, Math.max(keep.get(e.id) ?? 0, e.lvl));
  if (keep.size) out.ench = [...keep].map(([id, lvl]) => ({ id, lvl })); else delete out.ench;
  return { out, xp };
}

/** XP actually refunded for an `xp` upper bound. */
export const grindstoneRefund = (xp: number, rnd: () => number): number => {
  const half = Math.ceil(xp / 2);
  return xp <= 0 ? 0 : half + Math.floor(rnd() * half);
};
